import React, { ComponentProps, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Button, ButtonModel, IconRegularCheckmark, IconRegularClock, Spinner } from '@notino/react-styleguide'
import { useForm } from 'react-hook-form'
import dayjs from 'dayjs'
import { FormattedMessage, useIntl } from 'react-intl'
import debounce from 'lodash.debounce'
import axios from 'axios'

// hooks
import useMessages from '../../../hooks/useMessages'

// styles
import * as SC from './Step2EmployeeAndDateSelectionStyles'

// components
import ReservationBookingModalLayout from '../ReservationBookingModalLayout/ReservationBookingModalLayout'
import HookFormField from '../../../formFields/HookFormField'
import DatePickerFormField from '../../../formFields/DatePickerFormField/DatePickerFormField'
import HeaderContent from '../HeaderContent/HeaderContent'
import ResourceCard from '../../ResourceCard/ResourceCard'
import CheckboxGroupCustomFormField from '../../../formFields/CheckboxGroupCustomFormField/CheckboxGroupCustomFormField'
import RenderPrice from '../../RenderPrice/RenderPrice'
import RenderDuration from '../../RenderDuration/RenderDuration'
import TimeSlotOptionsSkeleton from '../TimeSlotOptionsSkeleton/TimeSlotOptionsSkeleton'
import Empty from '../../../atoms/Empty/Empty'
import ErrorScreen from '../../../atoms/ErrorScreen/ErrorScreen'
import ReservationBookingDialog from '../ReservationBookingDialog/ReservationBookingDialog'
import * as DialogSC from '../ReservationBookingDialog/ReservationBookingDialogStyles'
import collapseMotion from './collapseMotion'
import VisuallyHidden from '../../../atoms/VisuallyHidden/VisuallyHidden'

// types
import {
	AllowedReservationTimeSlot,
	GetCalendarEventsReservationAvailableParams,
	GetCalendarEventsReservationAvailableResponse,
	PostCartReservationBody,
	ReservationBookingEmployeeAndDateSelectionForm,
	ReservationBookingEmployeeAndDateSelectionSubmittedForm,
	ReservationBookingSelectedFormValues,
	SalonServiceResponse,
	ServiceCategoryParameterValueData,
	TimeSlotOption
} from '../../../types/types'
import { CheckboxGroupCustomFormFieldProps } from '../../../formFields/CheckboxGroupCustomFormField/types'
import { OnChangeViewArgs, SelectedViewDates } from '../../../atoms/DatePicker/types'
import { DatePickerFormFieldProps } from '../../../formFields/DatePickerFormField/types'

// utils
import {
	ABORT_KEYS,
	CALENDAR_VIEW_CHANGE_DEBOUNCE_TIME_MS,
	CART_RESERVATION_SUBMIT_ERROR_CODE,
	DEFAULT_DATE_FORMAT,
	FORM,
	MSG_TYPE,
	RESERVATION_BOOKING_MODAL_STEP,
	RESERVATIONS_TIME_SLOTS
} from '../../../utils/enums'
import { ERROR_MESSAGES, isEnumValue, showErrorNotifications, showNotifications, TIME_SLOTS_TRANSLATIONS } from '../../../utils/helper'
import { AppContext } from '../../../utils/appProvider'
import { abortControllers } from '../../../utils/request'
import {
	ANYONE_AVAILABLE_OPTION_VALUE,
	AvailableDatesStateData,
	EmployeeOption,
	getAnyoneAvailableOption,
	getCartReservationRequestBody,
	getCartReservationSubmitErrorCode,
	getCalendarGridKey,
	getEmployeeAndDateSelectionInitFormData,
	getEmployeeOptions,
	getFirstAvailableDateQueryParams,
	getSelectedTimeSlotOption,
	getSelectedTimeSlotValue,
	getTimeSlotData,
	parseCalendarGridKey,
	resolveEmptySlotAvailability,
	scrollElementIntoView,
	TIME_SLOT_PERIODS
} from '../reservationBookingModalHelpers'
import { getFullMonthGridDatesFromSelectedDates, getSelectedViewDates } from '../../../atoms/DatePicker/DatePicker'
import { pushToDataLayer } from '../../../utils/dataLayer'
import { getModalReservationCalendarConfirmEvent } from '../../../utils/dataLayerEvents'

// assets
import UserIcon from '../../../assets/icons/UserIcon'

type Props = PropsWithChildren<{
	salonID: string
	selectedFormValues: ReservationBookingSelectedFormValues
	selectedIndustryID: string | undefined
	selectedServiceData: SalonServiceResponse['service']
	selectedServiceCategoryParameterValueData: ServiceCategoryParameterValueData | undefined
	initAvResTimeSlotDayPart: RESERVATIONS_TIME_SLOTS | null
	headerImage: string | undefined
	isAddingAnotherServiceFromSummary: boolean
	maxCountCartReservations: number
	onViewReservationsSummary: () => void
	editingCartReservationItemID: string | null
	onClose: () => void
	onBackButtonClick?: () => void
	handleSubmitEmployeeAndDateSelectionForm: (
		values: ReservationBookingEmployeeAndDateSelectionSubmittedForm,
		options?: { keepCartEditState?: boolean }
	) => void
	onPersistCartReservation: (
		requestBody: PostCartReservationBody,
		submittedData: ReservationBookingEmployeeAndDateSelectionSubmittedForm,
		cartReservationItemID: string | null
	) => Promise<void>
}>

const EMPLOYEE_COLLAPSE_KEY = 'EMPLOYEE_COLLAPSE_KEY'
const EMPLOYEE_COLLAPSE_TRIGGER_CLASS = 'employee-collapse-trigger'

const TIME_SLOT_EMPLOYEE_WRAPPER_ID = 'TIME_SLOT_EMPLOYEE_WRAPPER_ID'

const employeeOptionRender = (option: EmployeeOption, isChecked: boolean, isDisabled: boolean, isTimeSlotEmployee: boolean) => {
	return (
		<SC.EmployeeOptionWrapper $isDisabled={isDisabled} $isTimeSlotEmployee={isTimeSlotEmployee}>
			<ResourceCard
				extraContent={isChecked ? <IconRegularCheckmark /> : null}
				title={option.label}
				avatarUrl={option.extra?.avatar}
				customIcon={<UserIcon />}
				infoContent={
					<SC.PriceAndDurationWrapper>
						<SC.DurationWrapper>
							<IconRegularClock color={'icon.secondary'} width={'16px'} height={'16px'} aria-hidden />
							<RenderDuration from={option.extra?.priceAndDurationData?.durationFrom} to={option.extra?.priceAndDurationData?.durationTo} />
						</SC.DurationWrapper>
						<RenderPrice
							from={option.extra?.priceAndDurationData?.priceFrom}
							to={option.extra?.priceAndDurationData?.priceTo}
							fromSecondary={option.extra?.priceAndDurationData?.priceFromSecondary}
							toSecondary={option.extra?.priceAndDurationData?.priceToSecondary}
						/>
					</SC.PriceAndDurationWrapper>
				}
			/>
		</SC.EmployeeOptionWrapper>
	)
}

const DEFAULT_CALENDAR_VIEW: DatePickerFormFieldProps['defaultView'] = 'weekly'

const Step2EmployeeAndDateSelection = (props: Props) => {
	const {
		onClose,
		onBackButtonClick,
		handleSubmitEmployeeAndDateSelectionForm,
		initAvResTimeSlotDayPart,
		salonID,
		selectedFormValues,
		selectedIndustryID,
		selectedServiceData,
		selectedServiceCategoryParameterValueData,
		headerImage,
		isAddingAnotherServiceFromSummary,
		maxCountCartReservations,
		onViewReservationsSummary,
		editingCartReservationItemID,
		onPersistCartReservation
	} = props

	const { formatDate, formatMessage } = useIntl()
	const { messages } = useMessages()
	const { apiBrowser, isAuthorized } = useContext(AppContext)

	const submittedFormData = getEmployeeAndDateSelectionInitFormData(selectedFormValues[RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION])

	const dateValue = dayjs(submittedFormData.date)
	const dateValueString = dateValue.format(DEFAULT_DATE_FORMAT)

	const submittedValueCalendarViewSelectedDays = getSelectedViewDates(dateValue, DEFAULT_CALENDAR_VIEW === 'weekly')
	const submittedValueCalendarViewGridDates = getFullMonthGridDatesFromSelectedDates(submittedValueCalendarViewSelectedDays, dateValue)
	const submittedValueCalendarViewGridKey = getCalendarGridKey(
		submittedValueCalendarViewGridDates.startDate,
		submittedValueCalendarViewGridDates.endDate,
		submittedFormData.employeeID[0]
	)

	const [selectedCalendarViewGrid, setSelectedCalendarViewGrid] = useState<SelectedViewDates>(submittedValueCalendarViewGridDates)
	const [availableDatesData, setAvailableDatesData] = useState<AvailableDatesStateData>({ [submittedValueCalendarViewGridKey]: null })
	const [employeeFirstAvailableDate, setEmployeeFirstAvailableDate] = useState<string | null>(null)
	const [anyoneFirstAvailableDate, setAnyoneFirstAvailableDate] = useState<string | null>(null)
	const [isResolvingEmptyState, setIsResolvingEmptyState] = useState(false)

	const [employeeCollapseActiveKeys, setEmployeeCollapseActiveKeys] = useState<React.Key[]>([])
	const [timeSlotsCollapseActiveKeys, setTimeSlotsCollapseActiveKeys] = useState<AllowedReservationTimeSlot[]>(
		isEnumValue(initAvResTimeSlotDayPart, RESERVATIONS_TIME_SLOTS) && initAvResTimeSlotDayPart !== RESERVATIONS_TIME_SLOTS.ANY
			? [initAvResTimeSlotDayPart]
			: []
	)

	const [isLoading, setIsLoading] = useState(true)
	const [loadingError, setLoadingError] = useState(false)
	const [tryAgain, setTryAgain] = useState(false)
	const [isSubmittingCartReservation, setIsSubmittingCartReservation] = useState(false)
	const [isAddReservationErrorModalOpen, setIsAddReservationErrorModalOpen] = useState(false)
	const [isFullCartModalOpen, setIsFullCartModalOpen] = useState(false)
	const [isServiceAlreadyBookedModalOpen, setIsServiceAlreadyBookedModalOpen] = useState(false)
	const retryAddReservationRef = useRef<(() => Promise<void>) | null>(null)

	const isInitialTimeSlotRequest = useRef(true)
	const employeeCollapseRef = useRef<HTMLDivElement | null>(null)

	const singleEmployee = selectedServiceData.employees.length === 1

	const { control, handleSubmit, watch, setValue } = useForm<ReservationBookingEmployeeAndDateSelectionForm>({
		defaultValues: submittedFormData,
		values: submittedFormData
	})

	const formValues = watch()
	const {
		date,
		employeeID: employeeIDValue,
		'timeSlot-MORNING': timeSlotMorning,
		'timeSlot-AFTERNOON': timeSlotAfternoon,
		'timeSlot-EVENING': timeSlotEvening,
		timeSlotEmployee
	} = formValues
	const [employeeID] = employeeIDValue

	const currentCalendarViewGridKey = getCalendarGridKey(selectedCalendarViewGrid.startDate, selectedCalendarViewGrid.endDate, employeeID)

	const availableDatesForDatePicker = useMemo<DatePickerFormFieldProps['availableDates']>(
		() =>
			(availableDatesData[currentCalendarViewGridKey] || []).map((item) => ({
				date: item.date,
				available: !!item.items.length
			})),
		[availableDatesData, currentCalendarViewGridKey]
	)

	const currentValueCalendarViewGridDates = getFullMonthGridDatesFromSelectedDates({ startDate: date, endDate: date }, date)
	const currentValueCalendarViewGridKey = getCalendarGridKey(
		currentValueCalendarViewGridDates.startDate,
		currentValueCalendarViewGridDates.endDate,
		employeeID
	)

	const fetchEmptyStateAvailability = useCallback(
		async (dateFrom: string, employeeIdForRequest: string) => {
			const { FIRST_AVAILABLE_DATE_ABORT_KEY } = ABORT_KEYS

			abortControllers.httpGet.get(FIRST_AVAILABLE_DATE_ABORT_KEY)?.abort()

			setEmployeeFirstAvailableDate(null)
			setAnyoneFirstAvailableDate(null)
			setIsResolvingEmptyState(true)

			let wasCanceled = false

			try {
				const result = await resolveEmptySlotAvailability(
					(salonId, query) =>
						apiBrowser.b2c.getCalendarEventsReservationFirstAvailableDate(salonId, query, {
							allowAbort: true,
							abortSignalKey: FIRST_AVAILABLE_DATE_ABORT_KEY
						}),
					salonID,
					{
						dateFrom,
						serviceID: selectedServiceData.id,
						serviceCategoryParameterValueID: selectedServiceCategoryParameterValueData?.id,
						employeeID: employeeIdForRequest,
						hasMultipleEmployees: !singleEmployee
					}
				)

				setEmployeeFirstAvailableDate(result.employeeFirstAvailableDate)
				setAnyoneFirstAvailableDate(result.anyoneFirstAvailableDate)
			} catch (error) {
				if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
					wasCanceled = true
					return
				}
			} finally {
				if (!wasCanceled) {
					setIsResolvingEmptyState(false)
				}
			}
		},
		[apiBrowser.b2c, salonID, selectedServiceData.id, selectedServiceCategoryParameterValueData?.id, singleEmployee]
	)

	useEffect(() => {
		;(async () => {
			const cachedData = availableDatesData[currentCalendarViewGridKey]

			// no need to fetch data that already exits
			if (cachedData) {
				const hasSlotsForSelectedDate = cachedData.some((item) => dayjs(item.date).isSame(date, 'date') && item.items.length > 0)

				if (!hasSlotsForSelectedDate) {
					await fetchEmptyStateAvailability(date.format(DEFAULT_DATE_FORMAT), employeeID)
				}

				return
			}

			// abort previous requests in case the new one is called
			const { AVAILABLE_SLOT_REQUEST_1_ABORT_KEY, AVAILABLE_SLOT_REQUEST_2_ABORT_KEY } = ABORT_KEYS

			abortControllers.httpGet.get(AVAILABLE_SLOT_REQUEST_1_ABORT_KEY)?.abort()
			abortControllers.httpGet.get(AVAILABLE_SLOT_REQUEST_2_ABORT_KEY)?.abort()

			setIsLoading(true)
			setLoadingError(false)
			// BE allows maximum range of 31 days, so we need to split it to 2 requests if the range is higher
			const requests: Promise<GetCalendarEventsReservationAvailableResponse | undefined>[] = []

			const commonRequestsParams: Omit<GetCalendarEventsReservationAvailableParams, 'dateFrom' | 'dateTo'> = {
				serviceID: selectedServiceData.id,
				employeeID: employeeID === ANYONE_AVAILABLE_OPTION_VALUE ? undefined : employeeID,
				serviceCategoryParameterValueID: selectedServiceCategoryParameterValueData?.id
			}

			// BE doesn't allow to send dateFrom that is earlier than today
			// BE doesn't allow to send dateTo eelier than dateFrom
			const dateFromForRequest = selectedCalendarViewGrid.startDate.isBefore(dayjs(), 'date') ? dayjs() : selectedCalendarViewGrid.startDate
			const dateToForRequest = selectedCalendarViewGrid.endDate.isBefore(dateFromForRequest, 'date')
				? dateFromForRequest
				: selectedCalendarViewGrid.endDate

			if (dateToForRequest.diff(dateFromForRequest, 'days') > 31) {
				requests.push(
					apiBrowser.b2c.getCalendarEventsReservationAvailableParamsSlots(
						salonID,
						{
							...commonRequestsParams,
							dateFrom: dateFromForRequest.format(DEFAULT_DATE_FORMAT),
							dateTo: dateFromForRequest.add(31, 'days').format(DEFAULT_DATE_FORMAT)
						},
						{ allowAbort: true, abortSignalKey: AVAILABLE_SLOT_REQUEST_1_ABORT_KEY }
					)
				)
				requests.push(
					apiBrowser.b2c.getCalendarEventsReservationAvailableParamsSlots(
						salonID,
						{
							...commonRequestsParams,
							dateFrom: dateFromForRequest.add(32, 'days').format(DEFAULT_DATE_FORMAT),
							dateTo: dateToForRequest.format(DEFAULT_DATE_FORMAT)
						},
						{ allowAbort: true, abortSignalKey: AVAILABLE_SLOT_REQUEST_2_ABORT_KEY }
					)
				)
			} else {
				requests.push(
					apiBrowser.b2c.getCalendarEventsReservationAvailableParamsSlots(
						salonID,
						{
							...commonRequestsParams,
							dateFrom: dateFromForRequest.format(DEFAULT_DATE_FORMAT),
							dateTo: dateToForRequest.format(DEFAULT_DATE_FORMAT)
						},
						{ allowAbort: true, abortSignalKey: AVAILABLE_SLOT_REQUEST_1_ABORT_KEY }
					)
				)
			}

			try {
				const response = await Promise.all(requests)
				const filteredResponse = response.reduce<GetCalendarEventsReservationAvailableResponse[]>((acc, cv) => {
					return cv ? [...acc, cv] : acc
				}, [])

				const mergedResponseData = filteredResponse.reduce<GetCalendarEventsReservationAvailableResponse['availableReservations']>((acc, cv) => {
					return [...acc, ...cv.availableReservations]
				}, [])

				setAvailableDatesData((current) => ({
					...current,
					[currentCalendarViewGridKey]: mergedResponseData
				}))

				// find the closest available date when the component is first time initialized and there are no available slots for the initially selected date
				if (isInitialTimeSlotRequest.current) {
					if (mergedResponseData.find((item) => dayjs(item.date).isSame(dayjs(), 'date') && !item.items.length)) {
						const firstAvailableDateResponse = await apiBrowser.b2c.getCalendarEventsReservationFirstAvailableDate(
							salonID,
							getFirstAvailableDateQueryParams({
								dateFrom: dateValueString,
								serviceID: selectedServiceData.id,
								serviceCategoryParameterValueID: selectedServiceCategoryParameterValueData?.id,
								employeeID
							})
						)

						// no need to show error state when this request fails, because it is only nice to have feature
						if (firstAvailableDateResponse?.firstAvailableDate) {
							const newFirstAvailableDate = dayjs(firstAvailableDateResponse.firstAvailableDate)

							setValue('date', dayjs(newFirstAvailableDate))

							// if new firstAvailableDate is not in the range we fetched for the first time, we want to continue showing loading state
							// calendar view will be changed, which will re-trigger this useEffect a new data will be fetched
							if (!newFirstAvailableDate.isBetween(selectedCalendarViewGrid.startDate, selectedCalendarViewGrid.endDate, 'date', '[]')) {
								isInitialTimeSlotRequest.current = false
								return
							}
						}
					}

					isInitialTimeSlotRequest.current = false
				}

				const hasSlotsForSelectedDate = mergedResponseData.some((item) => dayjs(item.date).isSame(date, 'date') && item.items.length > 0)

				if (!hasSlotsForSelectedDate) {
					await fetchEmptyStateAvailability(date.format(DEFAULT_DATE_FORMAT), employeeID)
				}

				setIsLoading(false)
			} catch (error) {
				// set error, but only if it is not caused by cancel token
				if (axios.isAxiosError(error) && error.code !== 'ERR_CANCELED') {
					setLoadingError(true)
					setIsLoading(false)
				}
			}
		})()
	}, [
		dateValueString,
		date,
		selectedCalendarViewGrid.startDate,
		selectedCalendarViewGrid.endDate,
		selectedServiceData.id,
		salonID,
		employeeID,
		setValue,
		selectedServiceCategoryParameterValueData?.id,
		apiBrowser,
		availableDatesData,
		currentCalendarViewGridKey,
		fetchEmptyStateAvailability,
		// tryAgain is just a listener that forces to re-run use effect when request fails
		tryAgain
	])

	// Button from Notino style-guide library doesn't recognize "form" as a prop, however, when passed to the component, it works fine
	const unknownButtonProps = {
		form: FORM.RESERVATION_BOOKING_MODAL_EMPLOYEE_AND_DATE_SELECTION
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} as any

	const { timeSlotOptions, timeSlotDataMap } = useMemo(() => {
		let gridKeyData: AvailableDatesStateData[number] = availableDatesData[currentValueCalendarViewGridKey]

		if (!gridKeyData) {
			Object.entries(availableDatesData).forEach(([key, data]) => {
				const { startDate, endDate, employeeID: employeeIdKey } = parseCalendarGridKey(key)
				if (date.isBetween(startDate, endDate, 'date', '[]') && employeeIdKey === employeeID) {
					gridKeyData = data
				}
			})
		}

		return getTimeSlotData(gridKeyData || [], date)
	}, [availableDatesData, currentValueCalendarViewGridKey, date, employeeID])

	const selectedTimeSlotOption = getSelectedTimeSlotOption([timeSlotMorning, timeSlotAfternoon, timeSlotEvening])
	const selectedTimeSlotOptionData = selectedTimeSlotOption ? timeSlotDataMap[selectedTimeSlotOption.value] : selectedTimeSlotOption
	const selectedTimeSlotValue = getSelectedTimeSlotValue(selectedTimeSlotOptionData, timeSlotEmployee)
	const parameterName =
		selectedServiceCategoryParameterValueData?.value && selectedServiceData.serviceCategoryParameter?.name
			? `${selectedServiceData.serviceCategoryParameter.name}: ${selectedServiceCategoryParameterValueData.value}`
			: selectedServiceCategoryParameterValueData?.value
	const disabledConfirmButton = !date || !employeeID || !selectedTimeSlotValue || isSubmittingCartReservation
	const isSlotsRegionBusy = isLoading || isResolvingEmptyState

	useEffect(() => {
		const newActiveKeys: AllowedReservationTimeSlot[] = []

		if (initAvResTimeSlotDayPart && initAvResTimeSlotDayPart !== RESERVATIONS_TIME_SLOTS.ANY) {
			// select day period keys based on user filters in salons category page
			newActiveKeys.push(initAvResTimeSlotDayPart)
		} else {
			// or open ones that have some data
			Object.entries(timeSlotOptions).forEach(([dayPeriod, options]) => {
				if (options.length) {
					newActiveKeys.push(dayPeriod as AllowedReservationTimeSlot)
				}
			})
		}
		setTimeSlotsCollapseActiveKeys(newActiveKeys)
	}, [timeSlotOptions, initAvResTimeSlotDayPart])

	const anyoneAvailableOption = getAnyoneAvailableOption(selectedServiceData, selectedServiceCategoryParameterValueData)

	const timeSlotDayPeriods = TIME_SLOT_PERIODS(TIME_SLOTS_TRANSLATIONS())

	const employeeOptions = getEmployeeOptions(
		anyoneAvailableOption,
		selectedServiceData,
		selectedServiceCategoryParameterValueData,
		singleEmployee ? [selectedServiceData.employees[0].id] : undefined
	)

	const selectedEmployeeData =
		employeeOptions.find((employeeOption) => {
			const employeeIdToCompare = employeeID
			return employeeIdToCompare === employeeOption.value
		}) || anyoneAvailableOption

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const onCalendarViewChange = useCallback(
		debounce((args: OnChangeViewArgs) => {
			setSelectedCalendarViewGrid(args.selectedViewMonthGridDates)
		}, CALENDAR_VIEW_CHANGE_DEBOUNCE_TIME_MS),
		[]
	)

	const afterDatePickerValueChange: DatePickerFormFieldProps['afterChange'] = async (newDate, dateCellData) => {
		abortControllers.httpGet.get(ABORT_KEYS.FIRST_AVAILABLE_DATE_ABORT_KEY)?.abort()
		setEmployeeFirstAvailableDate(null)
		setAnyoneFirstAvailableDate(null)
		setIsResolvingEmptyState(false)

		if (!dateCellData.isAvailable && !dateCellData.isPast) {
			await fetchEmptyStateAvailability(dayjs(newDate).format(DEFAULT_DATE_FORMAT), employeeID)
		}

		// reset time slot employee before every change
		setValue('timeSlotEmployee', [])

		// reset time slot values whenever date changes
		timeSlotDayPeriods.forEach((period) => {
			setValue(period.formName, [])
		})
	}

	const afterTimeSlotOptionValueChange: CheckboxGroupCustomFormFieldProps<TimeSlotOption[], TimeSlotOption>['afterChange'] = (newValue, option) => {
		// reset time slot employee before every change
		setValue('timeSlotEmployee', [])

		// reset all time slot values expect selected
		timeSlotDayPeriods.forEach((period) => {
			if (period.value !== option.extra?.dayPeriod) {
				setValue(period.formName, [])
			}
		})

		// Multi-employee slot: scroll specialist into view; keep focus on the selected time slot
		if (!newValue.length) {
			return
		}

		const slotsForSelectedTime = timeSlotDataMap[option.value]
		if ((slotsForSelectedTime?.length || 0) > 1) {
			setTimeout(() => {
				const element = document.getElementById(TIME_SLOT_EMPLOYEE_WRAPPER_ID)
				if (element) {
					scrollElementIntoView(element)
				}
			}, 100)
		}
	}

	const afterEmployeeChange: CheckboxGroupCustomFormFieldProps<string[], EmployeeOption>['afterChange'] = () => {
		// Move focus to the collapse header before radios unmount so it never falls to the page CTA.
		employeeCollapseRef.current?.querySelector<HTMLElement>(`.${EMPLOYEE_COLLAPSE_TRIGGER_CLASS}`)?.focus({ preventScroll: true })
		setEmployeeCollapseActiveKeys([])
		abortControllers.httpGet.get(ABORT_KEYS.FIRST_AVAILABLE_DATE_ABORT_KEY)?.abort()
		setEmployeeFirstAvailableDate(null)
		setAnyoneFirstAvailableDate(null)
		setIsResolvingEmptyState(false)

		// reset time slot employee before every change
		setValue('timeSlotEmployee', [])

		// reset all time slot values expect selected
		timeSlotDayPeriods.forEach((period) => {
			setValue(period.formName, [])
		})
	}

	const reloadTimeSlots = useCallback(() => {
		setAvailableDatesData((current) => {
			const nextAvailableDatesData = { ...current }
			delete nextAvailableDatesData[currentCalendarViewGridKey]
			delete nextAvailableDatesData[currentValueCalendarViewGridKey]
			return nextAvailableDatesData
		})

		timeSlotDayPeriods.forEach((period) => {
			setValue(period.formName, [])
		})
		setValue('timeSlotEmployee', [])
		setTryAgain((current) => !current)
	}, [currentCalendarViewGridKey, currentValueCalendarViewGridKey, setValue, timeSlotDayPeriods])

	const submitCartReservation = async (requestData: {
		requestBody: PostCartReservationBody
		submittedData: ReservationBookingEmployeeAndDateSelectionSubmittedForm
	}) => {
		setIsSubmittingCartReservation(true)

		const submitRequest = async () => {
			await onPersistCartReservation(requestData.requestBody, requestData.submittedData, editingCartReservationItemID)
		}

		try {
			await submitRequest()
		} catch (error) {
			const errorCode = getCartReservationSubmitErrorCode(error)

			if (errorCode === CART_RESERVATION_SUBMIT_ERROR_CODE.CART_IS_FULL) {
				setIsFullCartModalOpen(true)
				return
			}

			if (errorCode === CART_RESERVATION_SUBMIT_ERROR_CODE.ALREADY_BOOKED) {
				setIsServiceAlreadyBookedModalOpen(true)
				return
			}

			if (isAddingAnotherServiceFromSummary || editingCartReservationItemID) {
				retryAddReservationRef.current = submitRequest
				setIsAddReservationErrorModalOpen(true)
				return
			}

			showErrorNotifications(error, { force: true })
		} finally {
			setIsSubmittingCartReservation(false)
		}
	}

	const handleFullCartModalClose = () => {
		setIsFullCartModalOpen(false)
	}

	const handleFullCartModalViewReservations = () => {
		setIsFullCartModalOpen(false)
		onViewReservationsSummary()
	}

	const handleServiceAlreadyBookedModalConfirm = () => {
		setIsServiceAlreadyBookedModalOpen(false)
		reloadTimeSlots()
	}

	const handleAddReservationErrorModalClose = () => {
		if (isSubmittingCartReservation) {
			return
		}

		setIsAddReservationErrorModalOpen(false)
		retryAddReservationRef.current = null
	}

	const handleAddReservationErrorModalRetry = async () => {
		const retry = retryAddReservationRef.current

		if (!retry) {
			return
		}

		setIsSubmittingCartReservation(true)

		try {
			await retry()
			setIsAddReservationErrorModalOpen(false)
			retryAddReservationRef.current = null
		} catch {
			// Keep the error modal open for another retry attempt.
		} finally {
			setIsSubmittingCartReservation(false)
		}
	}

	const handleSubmitForm = async (values: ReservationBookingEmployeeAndDateSelectionForm) => {
		if (!selectedTimeSlotValue) {
			// The application shouldn't be able to reach this state
			// But to avoid having just a return with nothing happening, an error message is displayed here as a fallback
			showNotifications([{ type: MSG_TYPE.ERROR, message: ERROR_MESSAGES().GENERAL_ERROR }])
			return
		}

		const submitDataEmployeeID = values.employeeID[0] !== ANYONE_AVAILABLE_OPTION_VALUE ? values.employeeID[0] : null
		const selectedDate = values.date.format(DEFAULT_DATE_FORMAT)
		const selectedEmployeeID = submitDataEmployeeID || selectedTimeSlotValue.employeeID

		const submittedData: ReservationBookingEmployeeAndDateSelectionSubmittedForm = {
			employeeID: submitDataEmployeeID,
			date: selectedDate,
			timeSlot: selectedTimeSlotValue
		}

		pushToDataLayer(
			getModalReservationCalendarConfirmEvent({
				category: selectedIndustryID,
				subcategory: selectedServiceData.category.child?.id,
				type: selectedServiceData.category.child.child?.id,
				subtype: selectedServiceCategoryParameterValueData?.categoryParameterValueID,
				employee_id: selectedEmployeeID,
				date: selectedDate,
				slot: `${selectedTimeSlotValue.timeFrom} - ${selectedTimeSlotValue.timeTo}`
			})
		)

		const requestBody = getCartReservationRequestBody({
			date: selectedDate,
			timeFrom: selectedTimeSlotValue.timeFrom,
			timeTo: selectedTimeSlotValue.timeTo,
			salonID,
			employeeID: selectedEmployeeID,
			serviceID: selectedServiceData.id,
			serviceCategoryParameterValueID: selectedServiceCategoryParameterValueData?.id ?? null
		})

		if (!isAuthorized) {
			if (editingCartReservationItemID || isAddingAnotherServiceFromSummary) {
				await submitCartReservation({ requestBody, submittedData })
				return
			}

			handleSubmitEmployeeAndDateSelectionForm(submittedData)
			return
		}

		await submitCartReservation({ requestBody, submittedData })
	}

	const getTimeSlotOptionsContent = () => {
		if (isSlotsRegionBusy) {
			return <TimeSlotOptionsSkeleton />
		}

		const areTimeSlotsEmpty = Object.values(timeSlotOptions).every((options) => !options.length)

		if (areTimeSlotsEmpty) {
			const isSpecificEmployee = employeeID && employeeID !== ANYONE_AVAILABLE_OPTION_VALUE
			const hasEmployeeFutureDate = !!employeeFirstAvailableDate && !dayjs(employeeFirstAvailableDate).isSameOrBefore(date, 'date')
			const showChangeEmployeeCTA = !singleEmployee && !!isSpecificEmployee && !hasEmployeeFutureDate && !!anyoneFirstAvailableDate

			let emptyProps: ComponentProps<typeof Empty> = {
				title: messages['No available time slots'],
				label: messages['There are currently no available time slots']
			}

			if (hasEmployeeFutureDate) {
				emptyProps = {
					...emptyProps,
					title: messages['No available time slots on this day'],
					label: (
						<FormattedMessage
							id='Next available time slot is on <button>{ date }</button>'
							defaultMessage='Next available time slot is on <button>{ date }</button>'
							values={{
								date: formatDate(dayjs(employeeFirstAvailableDate).toDate(), { dateStyle: 'medium' }),
								button: (chunks) => (
									<SC.EmptyStateLinkButton
										type={'button'}
										onClick={() => {
											// there is a weird screen flick when it jumps immediately from empty state to the open collapse with time slots, so there's fake loading for a few hundred ms, so it feels more natural for the user
											setIsLoading(true)
											setTimeout(() => {
												setValue('date', dayjs(employeeFirstAvailableDate))
												setIsLoading(false)
											}, 200)
										}}
									>
										{chunks}
									</SC.EmptyStateLinkButton>
								)
							}}
						/>
					)
				}
			} else if (showChangeEmployeeCTA) {
				emptyProps = {
					title: messages['No available time slots'],
					label: messages['There are currently no available time slots for this employee. Please select another employee.'],
					button: {
						label: messages['Change employee'],
						onClick: () => {
							if (employeeCollapseRef.current) {
								employeeCollapseRef.current?.querySelector<HTMLElement>(`.${EMPLOYEE_COLLAPSE_TRIGGER_CLASS}`)?.focus({ preventScroll: false })
								scrollElementIntoView(employeeCollapseRef.current)
							}

							setEmployeeCollapseActiveKeys([EMPLOYEE_COLLAPSE_KEY])
						}
					}
				}
			}

			return <Empty minHeight={'200px'} {...emptyProps} />
		}

		return (
			<SC.TimeSlotsGroup role={'group'} aria-label={messages['Select time']}>
				<SC.TimeSlotsCollapseWrapper
					openMotion={collapseMotion}
					expandIcon={(expandProps: { isActive?: boolean }) => (
						<SC.ChevronIcon $isActive={expandProps.isActive} color={'icon.tertiary'} width={'1rem'} height={'1rem'} aria-hidden />
					)}
					activeKey={timeSlotsCollapseActiveKeys}
					onChange={(newActiveKey) => {
						setTimeSlotsCollapseActiveKeys((Array.isArray(newActiveKey) ? newActiveKey : [newActiveKey]) as AllowedReservationTimeSlot[])
					}}
					items={timeSlotDayPeriods.map((period) => {
						const options = timeSlotOptions[period.value]
						const timeSlotEmployees = selectedTimeSlotOptionData || []
						const isSelectedOptionFromThisPeriod =
							selectedTimeSlotOption?.extra?.dayPeriod && selectedTimeSlotOption?.extra?.dayPeriod === period.value

						return {
							key: period.value,
							label: period.label,
							children: !options.length ? (
								<SC.DayPeriodEmptyState>{messages['No time slots available']}</SC.DayPeriodEmptyState>
							) : (
								<SC.CollapseContent>
									<HookFormField
										control={control}
										name={period.formName}
										singleSelect
										options={options}
										optionAsValue
										afterChange={afterTimeSlotOptionValueChange}
										component={CheckboxGroupCustomFormField<TimeSlotOption[], TimeSlotOption>}
									/>
									{isSelectedOptionFromThisPeriod &&
										timeSlotEmployees.length > 1 &&
										(() => {
											const timeSlotEmployeeIDs = timeSlotEmployees.map((slot) => slot.employeeID)
											const timeSlotEmployeeOptions = getEmployeeOptions(
												anyoneAvailableOption,
												selectedServiceData,
												selectedServiceCategoryParameterValueData,
												timeSlotEmployeeIDs
											)

											return (
												<SC.TimeSlotEmployeeWrapper id={TIME_SLOT_EMPLOYEE_WRAPPER_ID}>
													<HookFormField
														control={control}
														name={'timeSlotEmployee'}
														alignment={'vertical'}
														gap={'0'}
														singleSelect
														allowUnselect={false}
														label={
															<>
																<SC.EmployeeTitle>
																	{formatMessage(
																		{
																			id: 'Select a specialist for {time}',
																			defaultMessage: 'Select a specialist for {time}'
																		},
																		{ time: selectedTimeSlotOption?.value }
																	)}
																</SC.EmployeeTitle>
																<SC.EmployeeDescription>
																	{messages['You can choose from several specialists at this time']}
																</SC.EmployeeDescription>
															</>
														}
														// abelScreenReaderOnly
														options={timeSlotEmployeeOptions}
														component={CheckboxGroupCustomFormField<string[], EmployeeOption>}
														optionRender={(option: EmployeeOption, isChecked: boolean, isDisabled: boolean) =>
															employeeOptionRender(option, isChecked, isDisabled, true)
														}
													/>
												</SC.TimeSlotEmployeeWrapper>
											)
										})()}
								</SC.CollapseContent>
							)
						}
					})}
				/>
			</SC.TimeSlotsGroup>
		)
	}

	return (
		<>
			<ReservationBookingModalLayout
				title={messages.Date}
				headerImage={headerImage}
				onBackButtonClick={onBackButtonClick}
				onClose={onClose}
				headerContent={<HeaderContent title={selectedServiceData.category.child.child?.name ?? ''} description={parameterName} />}
				footerContent={
					<SC.FooterWrapper>
						<SC.DateTimeInfo>
							<SC.SelectedDate>{formatDate(dayjs(date).toDate(), { weekday: 'long', day: '2-digit', month: '2-digit' })}</SC.SelectedDate>
							<SC.SelectedTimeSlot>
								{selectedTimeSlotValue ? (
									<>
										{selectedTimeSlotValue.timeFrom} - {selectedTimeSlotValue.timeTo}
									</>
								) : null}
							</SC.SelectedTimeSlot>
						</SC.DateTimeInfo>
						{/* // Accept button */}
						<SC.ConfirmButton
							{...unknownButtonProps}
							type={'submit'}
							buttonStyle={ButtonModel.Styles.primary}
							disabled={disabledConfirmButton}
							aria-busy={isSubmittingCartReservation}
						>
							<SC.ConfirmButtonLabel $isLoading={isSubmittingCartReservation} aria-hidden={isSubmittingCartReservation}>
								{messages.Confirm}
							</SC.ConfirmButtonLabel>
							{isSubmittingCartReservation ? (
								<>
									<VisuallyHidden>{messages.Confirm}</VisuallyHidden>
									<SC.ConfirmButtonSpinner>
										<Spinner size={16} color={'icon.disabled'} aria-hidden />
									</SC.ConfirmButtonSpinner>
								</>
							) : null}
						</SC.ConfirmButton>
					</SC.FooterWrapper>
				}
			>
				{(() => {
					if (loadingError) {
						return <ErrorScreen button={{ onClick: () => setTryAgain((current) => !current) }} />
					}

					return (
						<SC.Form id={FORM.RESERVATION_BOOKING_MODAL_EMPLOYEE_AND_DATE_SELECTION} onSubmit={handleSubmit(handleSubmitForm)}>
							<SC.EmployeeCollapseWrapper
								ref={employeeCollapseRef}
								activeKey={employeeCollapseActiveKeys}
								openMotion={collapseMotion}
								$collapsible={!singleEmployee}
								onChange={(newActiveKeys) => {
									setEmployeeCollapseActiveKeys(Array.isArray(newActiveKeys) ? newActiveKeys : [newActiveKeys])
								}}
								collapsible={singleEmployee ? 'disabled' : 'header'}
								items={[
									{
										key: EMPLOYEE_COLLAPSE_KEY,
										classNames: {
											title: EMPLOYEE_COLLAPSE_TRIGGER_CLASS
										},
										label: (
											<SC.CardWrapper $collapsible={!singleEmployee}>
												{!singleEmployee ? <VisuallyHidden>{messages['Select the specialist']}</VisuallyHidden> : null}
												<ResourceCard
													customIcon={<UserIcon />}
													title={selectedEmployeeData.label}
													avatarUrl={selectedEmployeeData.extra?.avatar}
													infoContent={
														<SC.PriceAndDurationWrapper>
															<SC.DurationWrapper>
																<IconRegularClock color={'icon.secondary'} width={'16px'} height={'16px'} aria-hidden />
																<RenderDuration
																	from={selectedEmployeeData.extra?.priceAndDurationData?.durationFrom}
																	to={selectedEmployeeData.extra?.priceAndDurationData?.durationTo}
																/>
															</SC.DurationWrapper>
															<RenderPrice
																from={selectedEmployeeData.extra?.priceAndDurationData?.priceFrom}
																to={selectedEmployeeData.extra?.priceAndDurationData?.priceTo}
																fromSecondary={selectedEmployeeData.extra?.priceAndDurationData?.priceFromSecondary}
																toSecondary={selectedEmployeeData.extra?.priceAndDurationData?.priceToSecondary}
															/>
														</SC.PriceAndDurationWrapper>
													}
													extraContent={
														singleEmployee ? null : (
															<SC.ChevronIcon
																$isActive={!!employeeCollapseActiveKeys.length}
																color={'icon.primary'}
																width={'1.5rem'}
																height={'1.5rem'}
																aria-hidden
															/>
														)
													}
												/>
											</SC.CardWrapper>
										),
										children: (
											<HookFormField
												control={control}
												name={'employeeID'}
												readOnly={isLoading}
												component={CheckboxGroupCustomFormField<string[], EmployeeOption>}
												options={employeeOptions}
												optionRender={(option: EmployeeOption, isChecked: boolean, isDisabled: boolean) =>
													employeeOptionRender(option, isChecked, isDisabled, false)
												}
												afterChange={afterEmployeeChange}
												alignment={'vertical'}
												singleSelect
												gap={'0'}
												allowUnselect={false}
												label={messages['Select the specialist']}
												labelScreenReaderOnly
											/>
										)
									}
								]}
							/>
							<HookFormField
								control={control}
								name={'date'}
								component={DatePickerFormField}
								label={messages.Date}
								labelScreenReaderOnly
								afterChange={afterDatePickerValueChange}
								size={'small'}
								availableDates={availableDatesForDatePicker}
								onChangeView={onCalendarViewChange}
								isLoading={isLoading}
								defaultView={DEFAULT_CALENDAR_VIEW}
							/>
							<SC.Divider />
							<SC.TimeSlotsRegion aria-busy={isSlotsRegionBusy || undefined}>
								{isSlotsRegionBusy ? (
									<VisuallyHidden role={'status'}>{formatMessage({ id: 'Loading', defaultMessage: 'Loading' })}</VisuallyHidden>
								) : null}
								{getTimeSlotOptionsContent()}
							</SC.TimeSlotsRegion>
						</SC.Form>
					)
				})()}
			</ReservationBookingModalLayout>
			<ReservationBookingDialog
				isOpen={isAddReservationErrorModalOpen}
				onClose={handleAddReservationErrorModalClose}
				showHeaderCloseButton
				isHeaderCloseButtonDisabled={isSubmittingCartReservation}
				title={formatMessage({ id: 'Failed to add the reservation', defaultMessage: 'Failed to add the reservation' })}
				description={formatMessage({
					id: 'We could not add the service to your reservation. Please try again.',
					defaultMessage: 'We could not add the service to your reservation. Please try again.'
				})}
				footer={
					<DialogSC.Footer>
						<DialogSC.FooterButton>
							<Button
								type={'button'}
								buttonStyle={ButtonModel.Styles.secondary}
								fullWidth
								disabled={isSubmittingCartReservation}
								onClick={handleAddReservationErrorModalClose}
							>
								{formatMessage({ id: 'Close booking modal', defaultMessage: 'Close' })}
							</Button>
						</DialogSC.FooterButton>
						<DialogSC.FooterButton>
							<Button
								type={'button'}
								buttonStyle={ButtonModel.Styles.primary}
								fullWidth
								disabled={isSubmittingCartReservation}
								aria-busy={isSubmittingCartReservation}
								onClick={handleAddReservationErrorModalRetry}
							>
								{isSubmittingCartReservation ? (
									<>
										<Spinner size={16} color={'icon.disabled'} aria-hidden />
										<VisuallyHidden>{formatMessage({ id: 'Try again', defaultMessage: 'Try again' })}</VisuallyHidden>
									</>
								) : (
									formatMessage({ id: 'Try again', defaultMessage: 'Try again' })
								)}
							</Button>
						</DialogSC.FooterButton>
					</DialogSC.Footer>
				}
			/>
			<ReservationBookingDialog
				isOpen={isFullCartModalOpen}
				onClose={handleFullCartModalClose}
				title={formatMessage({ id: "Couldn't add service", defaultMessage: "Couldn't add service" })}
				description={formatMessage(
					{
						id: 'You already have {maxCountReservations} services in your reservation. Try deleting a service first, then add this one.',
						defaultMessage: 'You already have {maxCountReservations} services in your reservation. Try deleting a service first, then add this one.'
					},
					{ maxCountReservations: maxCountCartReservations }
				)}
				footer={
					<DialogSC.Footer>
						<DialogSC.FooterButton>
							<Button type={'button'} buttonStyle={ButtonModel.Styles.secondary} fullWidth onClick={handleFullCartModalClose}>
								{formatMessage({ id: 'Close booking modal', defaultMessage: 'Close' })}
							</Button>
						</DialogSC.FooterButton>
						<DialogSC.FooterButton>
							<Button type={'button'} buttonStyle={ButtonModel.Styles.primary} fullWidth onClick={handleFullCartModalViewReservations}>
								{formatMessage({ id: 'View reservations', defaultMessage: 'View reservations' })}
							</Button>
						</DialogSC.FooterButton>
					</DialogSC.Footer>
				}
			/>
			<ReservationBookingDialog
				isOpen={isServiceAlreadyBookedModalOpen}
				onClose={handleServiceAlreadyBookedModalConfirm}
				title={formatMessage({ id: 'Service has been booked!', defaultMessage: 'Service has been booked!' })}
				description={formatMessage({
					id: 'Some of your services have been booked. Please select another time slot, change the employee or choose another service.',
					defaultMessage: 'Some of your services have been booked. Please select another time slot, change the employee or choose another service.'
				})}
				footer={
					<Button type={'button'} buttonStyle={ButtonModel.Styles.primary} fullWidth onClick={handleServiceAlreadyBookedModalConfirm}>
						{formatMessage({ id: 'OK', defaultMessage: 'OK' })}
					</Button>
				}
			/>
		</>
	)
}

export default Step2EmployeeAndDateSelection
